import { SceneManager } from './core/SceneManager.js';
import { SphereManager } from './core/SphereManager.js';
import { TextureLoader } from './utils/TextureLoader.js';
import { RaycastHelper } from './utils/RaycastHelper.js';
import { DataLoader } from './utils/DataLoader.js';
import { DOMSanitizer } from './utils/DOMSanitizer.js';
import { throttle } from './utils/PerformanceUtils.js';
import { ImageManager } from './managers/ImageManager.js';
import { TripodMaskManager } from './managers/TripodMaskManager.js';
import { HotspotManager } from './managers/HotspotManager.js';
import { NavigationManager } from './managers/NavigationManager.js';
import { GalleryManager } from './managers/GalleryManager.js';
import { GraphManager } from './managers/GraphManager.js';
import { LogoOverlayManager } from './managers/LogoOverlayManager.js';
import { UIController } from './ui/UIController.js';
import { CrosshairOverlay } from './ui/CrosshairOverlay.js';
import { PlacementControls } from './ui/PlacementControls.js';
import { SandboxPanel } from './ui/SandboxPanel.js';
import { ViewerOverlay } from './ui/ViewerOverlay.js';
import { ContextMenu } from './ui/ContextMenu.js';
import { EdgeContextMenu } from './ui/EdgeContextMenu.js';
import { RenameModal } from './ui/RenameModal.js';
import { DeleteConfirmModal } from './ui/DeleteConfirmModal.js';
import { SaveModal } from './ui/SaveModal.js';
import { SaveProgressOverlay } from './ui/SaveProgressOverlay.js';
import { Toast } from './ui/Toast.js';
import { ApiService } from './services/ApiService.js';

/**
 * Main application class that orchestrates all components
 */
export class App {
    constructor(mode = 'edit', jwtToken = null, apiEndpoint = null, experienceId = null, projectId = null, autoAttach = false) {
        // Mode
        this.mode = mode; // 'view' or 'edit'
        this.experienceId = experienceId; // Track if we're editing an existing experience

        // Gallery tracking (for save/update operations)
        this.galleryId = null; // Numeric ID - Set after first successful save
        this.galleryUuid = experienceId; // UUID - Used for API calls (from URL param)
        this.sceneId = null; // CDN path identifier (e.g., gallery_360/{sceneId})

        // Project attachment (for auto-attach feature)
        this.projectId = projectId; // Project ID for attach API call
        this.autoAttach = autoAttach; // Whether to call attach API after first save
        this.hasBeenAttached = false; // Track if attach has been called

        // Debug logs for auto-attach configuration
        console.log('[App] 🔧 Auto-attach configuration:', {
            projectId: this.projectId,
            autoAttach: this.autoAttach,
            projectIdType: typeof this.projectId,
            autoAttachType: typeof this.autoAttach
        });

        // Core components
        this.sceneManager = null;
        this.sphereManager = null;
        this.textureLoader = null;
        this.raycastHelper = null;
        this.dataLoader = null;
        this.apiService = new ApiService(jwtToken, apiEndpoint, mode);

        // Managers
        this.imageManager = null;
        this.tripodMaskManager = null;
        this.hotspotManager = null;
        this.navigationManager = null;
        this.galleryManager = null;
        this.graphManager = null;
        this.logoOverlayManager = null;

        // UI
        this.uiController = null;
        this.crosshairOverlay = null;
        this.placementControls = null;
        this.sandboxPanel = null;
        this.saveProgressOverlay = null;
        this.toast = null;

        // State
        this.isInitialized = false;
        this.selectedCustomImageSize = 'medium';
        this.projectTitle = ''; // Project/gallery title (set via save modal)
        this.hasUnsavedChanges = false; // Track if there are unsaved modifications

        // Event listeners tracking for cleanup
        this.eventListeners = new Map();
    }

    /**
     * Notify parent window about modification state (only in edit mode)
     * @param {boolean} hasChanges - Whether there are unsaved changes
     */
    notifyParentWindow(hasChanges) {
        // Only send notifications in edit mode
        if (this.mode !== 'edit') {
            return;
        }

        try {
            window.parent.postMessage({
                type: 'VIEWER_MODIFIED',
                hasChanges: hasChanges
            }, '*');
            console.log(`[App] 📤 Parent notified: hasChanges=${hasChanges}`);
        } catch (error) {
            console.warn('[App] Failed to notify parent window:', error);
        }
    }

    /**
     * Mark that changes have been made (only in edit mode)
     */
    markAsModified() {
        if (this.mode !== 'edit') {
            return;
        }

        if (!this.hasUnsavedChanges) {
            this.hasUnsavedChanges = true;
            this.notifyParentWindow(true);
            console.log('[App] ✏️ Marked as modified');
        }
    }

    /**
     * Mark that all changes have been saved (only in edit mode)
     */
    markAsSaved() {
        if (this.mode !== 'edit') {
            return;
        }

        if (this.hasUnsavedChanges) {
            this.hasUnsavedChanges = false;
            this.notifyParentWindow(false);
            console.log('[App] ✅ Marked as saved');
        }
    }

    /**
     * Add an event listener and track it for cleanup
     * @param {EventTarget} target - The event target (window, element, etc.)
     * @param {string} event - The event name
     * @param {Function} handler - The event handler
     * @param {Object} options - Optional addEventListener options
     */
    addEventListener(target, event, handler, options) {
        if (!target || !event || !handler) {
            console.warn('[App] Invalid addEventListener call', { target, event, handler });
            return;
        }

        target.addEventListener(event, handler, options);

        if (!this.eventListeners.has(target)) {
            this.eventListeners.set(target, []);
        }

        this.eventListeners.get(target).push({ event, handler, options });
    }

    /**
     * Remove all tracked event listeners
     */
    removeAllEventListeners() {
        for (const [target, listeners] of this.eventListeners.entries()) {
            listeners.forEach(({ event, handler, options }) => {
                try {
                    target.removeEventListener(event, handler, options);
                } catch (error) {
                    console.warn('[App] Error removing event listener:', error);
                }
            });
        }
        this.eventListeners.clear();
    }

    /**
     * Initialize the application
     */
    async init() {
        console.log(`🎯 Initializing 360 Viewer Application in ${this.mode.toUpperCase()} mode...`);

        // Check WebGL support
        if (!this.isWebGLSupported()) {
            this.showError('❌ WebGL not supported by this browser');
            return;
        }

        // Apply mode-specific configurations
        this.applyModeConfiguration();
        
        // Initialize UI
        this.uiController = new UIController();
        this.uiController.initializeDOMElements();
        
        // Initialize placement UI components
        this.crosshairOverlay = new CrosshairOverlay();
        this.placementControls = new PlacementControls();
        this.viewerOverlay = new ViewerOverlay();
        this.saveProgressOverlay = new SaveProgressOverlay();
        this.toast = new Toast();

        // Initialize modals and menus (edit mode only)
        if (this.mode === 'edit') {
            this.contextMenu = new ContextMenu();
            this.edgeContextMenu = new EdgeContextMenu();
            this.renameModal = new RenameModal();
            this.deleteConfirmModal = new DeleteConfirmModal();
            this.saveModal = new SaveModal();

            // Create file input for custom images
            this.customImageInput = document.createElement('input');
            this.customImageInput.type = 'file';
            this.customImageInput.accept = 'image/*';
            this.customImageInput.style.display = 'none';
            document.body.appendChild(this.customImageInput);

            this.addEventListener(this.customImageInput, 'change', (e) => this.handleCustomImageSelected(e));
        }

        // Initialize sandbox panel (only in edit mode)
        if (this.mode === 'edit') {
            this.sandboxPanel = new SandboxPanel();
            this.sandboxPanel.initialize();

            // Initialize graph manager after panel is ready
            const graphCanvas = document.getElementById('graph-canvas');
            if (graphCanvas) {
                this.graphManager = new GraphManager(graphCanvas);
                this.setupGraphCallbacks();
            }

            this.setupSandboxCallbacks();
        }

        // Initialize core components
        const container = document.getElementById('canvas-container');
        this.sceneManager = new SceneManager(container);
        this.sphereManager = new SphereManager(this.sceneManager.scene);
        this.textureLoader = new TextureLoader(this.sceneManager.renderer);
        this.dataLoader = new DataLoader();
        this.raycastHelper = new RaycastHelper(
            this.sceneManager.camera,
            this.sceneManager.getCanvas()
        );
        
        // Initialize managers
        this.imageManager = new ImageManager(this.sphereManager, this.textureLoader, this.sceneManager, this.toast);
        this.tripodMaskManager = new TripodMaskManager(this.sceneManager.scene, this.textureLoader);
        this.hotspotManager = new HotspotManager(
            this.sceneManager.scene,
            this.sceneManager.camera,
            this.textureLoader,
            this.raycastHelper,
            this.sphereManager
        );
        
        // Set UI components for hotspot manager
        this.hotspotManager.setUIComponents(this.crosshairOverlay, this.placementControls);
        
        this.navigationManager = new NavigationManager(
            this.sceneManager.scene,
            this.sceneManager.camera,
            this.raycastHelper,
            this.sphereManager
        );
        
        // Set UI components for navigation manager
        this.navigationManager.setUIComponents(this.crosshairOverlay, this.placementControls);
        this.galleryManager = new GalleryManager(this.imageManager);

        // Initialize logo overlay manager
        this.logoOverlayManager = new LogoOverlayManager();
        
        // Setup callbacks
        this.setupCallbacks();
        
        // Setup UI event listeners
        this.setupUIEventListeners();

        // Setup image controls (sandbox mode only)
        if (this.mode === 'edit') {
            this.setupImageControls();
        }
        
        // Setup window resize with throttling (120ms)
        const throttledResize = throttle(() => this.sceneManager.handleResize(), 120);
        this.addEventListener(window, 'resize', throttledResize);

        // Setup navigation marker click handling
        this.addEventListener(this.sceneManager.getCanvas(), 'click', (e) => this.handleCanvasClick(e));

        // Setup escape key handling for canceling actions
        this.addEventListener(document, 'keydown', (e) => {
            if (e.key === 'Escape') {
                // Cancel navigation connection creation if active
                if (this.navigationManager.isCreatingConnection) {
                    this.cancelConnection();
                }
                // Cancel graph connection mode if active
                else if (this.graphManager && this.graphManager.isInConnectionMode) {
                    this.graphManager.cancelConnectionMode();
                    this.uiController.updateStatus('❌ Sélection annulée', 'error');
                }
            }
        });
        
        // Start animation loop
        this.sceneManager.startAnimationLoop(() => this.onFrame());
        
        // // Initialize gallery
        // this.galleryManager.initialize(
        //     document.getElementById('gallery-panel'),
        //     document.getElementById('gallery-thumbnails'),
        //     document.getElementById('gallery-toggle-btn')
        // );
        
        this.isInitialized = true;
        console.log('✅ 360 Viewer Application initialized successfully!');

        // Notify parent window that viewer is ready with no unsaved changes (edit mode only)
        if (this.mode === 'edit') {
            this.notifyParentWindow(false);
        }
    }

    /**
     * Setup callbacks between managers
     */
    setupCallbacks() {
        // Scene manager callbacks
        this.sceneManager.onZoomChange = (level, maxLevel) => {
            this.uiController.updateZoomIndicator(level, maxLevel);
        };

        // Thumbnail creation callback (called immediately after image load, before UI)
        this.imageManager.onThumbnailNeeded = (imageData) => {
            this.galleryManager.createLowResThumbnail(imageData);
        };

        // Image manager callbacks
        this.imageManager.onImageAdded = (imageData) => {
            const imageIndex = this.imageManager.currentImageIndex;
            this.uiController.addImageButton(
                imageData,
                imageIndex,
                true,
                () => this.imageManager.switchToImage(imageIndex)
            );
            this.galleryManager.refresh();

            // Add node to graph (sandbox mode only)
            if (this.graphManager) {
                const label = imageData.alias || imageData.name;
                // Use thumbnail URL if available, fallback to url only for manually uploaded images (blob URLs)
                // Don't fallback for API images as url may be KTX2
                const isBlobUrl = imageData.url && imageData.url.startsWith('blob:');
                const thumbnailUrl = imageData.thumbnail_url || (isBlobUrl ? imageData.url : null);
                console.log(`[App] 🖼️ Adding graph node for "${label}":`, {
                    hasThumbnail: !!imageData.thumbnail_url,
                    thumbnailUrl: thumbnailUrl,
                    imageUrl: imageData.url,
                    isBlobUrl: isBlobUrl
                });
                this.graphManager.addNode(imageData.id, label, null, null, thumbnailUrl);
                // Set as selected if it's the current image
                if (this.imageManager.currentImageIndex === imageIndex) {
                    this.graphManager.setSelectedImage(imageData.id);
                }
                this.updateGraphInfo();
            }

            // Initialize transition sphere after first image (for smooth transitions)
            if (this.imageManager.images.length === 1 && this.sceneManager) {
                this.imageManager.initializeTransitionSphere(this.sceneManager.scene);
            }

            // Show hotspots for the newly added image (and hide others)
            // This prevents "ghost" hotspots from previous images
            if (this.hotspotManager) {
                this.hotspotManager.showForImage(imageIndex);
            }

            // Show navigation markers for the newly added image
            if (this.navigationManager) {
                this.navigationManager.showForImage(imageData);
            }
        };

        this.imageManager.onImageChanged = (index) => {
            this.uiController.updateImageButtonStyles(index);
            this.galleryManager.refresh();

            // Update selected image in graph (sandbox mode only)
            if (this.graphManager && this.imageManager.images[index]) {
                this.graphManager.setSelectedImage(this.imageManager.images[index].id);
                // Update connection button state (might change if new image has different available connections)
                this.updateConnectionButtonState();
            }

            // Update custom images display (sandbox mode only)
            if (this.mode === 'edit') {
                this.updateCustomImagesDisplay();
            }
        };
        
        this.imageManager.onTransitionStart = (imageData) => {
            const t0 = performance.now();
            console.log(`[PERF] 📤 onTransitionStart callback - START`);

            // Hide elements from current image
            const currentImage = this.imageManager.getCurrentImage();
            if (currentImage) {
                const t1 = performance.now();
                this.hotspotManager.hideForImage(this.imageManager.currentImageIndex);
                const t2 = performance.now();
                console.log(`[PERF] hideForImage (hotspot): ${(t2 - t1).toFixed(2)}ms`);

                this.navigationManager.hideForImage(currentImage);
                const t3 = performance.now();
                console.log(`[PERF] hideForImage (navigation): ${(t3 - t2).toFixed(2)}ms`);
            }

            const t4 = performance.now();
            console.log(`[PERF] onTransitionStart TOTAL: ${(t4 - t0).toFixed(2)}ms`);
        };
        
        this.imageManager.onTransitionComplete = (imageData) => {
            // Show elements for new image
            this.hotspotManager.showForImage(this.imageManager.currentImageIndex);
            this.navigationManager.showForImage(imageData);
            this.tripodMaskManager.ensureVisible();
        };
        
        // Hotspot manager callbacks
        this.hotspotManager.onHotspotPlaced = () => {
            this.uiController.updateStatus('✅ Custom image placed', 'success');
        };
        
        this.hotspotManager.onPlacementCancelled = () => {
            this.uiController.updateStatus('❌ Image placement cancelled', 'error');
        };
        
        // Navigation manager callbacks
        this.navigationManager.onConnectionCreated = (connection) => {
            this.uiController.updateStatus('✅ Connexion créée avec succès!', 'success');

            // Mark as modified
            this.markAsModified();

            // Add edge to graph (sandbox mode only)
            if (this.graphManager) {
                const startNode = this.graphManager.nodes.find(n => n.imageId === connection.startImage.id);
                const endNode = this.graphManager.nodes.find(n => n.imageId === connection.endImage.id);

                if (startNode && endNode) {
                    this.graphManager.addEdge(startNode.id, endNode.id);
                    this.updateGraphInfo();
                }

                // Exit connection mode after successful creation
                if (this.graphManager.isInConnectionMode) {
                    this.graphManager.cancelConnectionMode();
                }
            }

            // Restore sidebar to normal size
            if (this.sandboxPanel) {
                this.sandboxPanel.restore();
            }
        };
        
        this.navigationManager.onNavigationTriggered = (targetImage, isCreatingConnection) => {
            console.log('[App] 🎯 Navigation triggered!');
            console.log('  - Target image:', targetImage);
            console.log('  - Target image name:', targetImage?.name);
            console.log('  - Target image ID:', targetImage?.id);
            console.log('  - Is creating connection:', isCreatingConnection);

            if (isCreatingConnection) {
                this.uiController.updateStatus('🎯 Start point placed. Transitioning to destination...', 'success');
                setTimeout(() => {
                    this.imageManager.startTransition(targetImage);
                    setTimeout(() => {
                        // Continue connection creation after transition
                        const raycastTargets = this.sphereManager.getRaycastObjects();
                        this.navigationManager.continueCreation(
                            raycastTargets,
                            (cursor) => this.sceneManager.setCursor(cursor)
                        );
                        this.uiController.updateStatus('🎯 Navigate to position and click Confirm to place the end point', 'success');
                    }, 1000);
                }, 100);
            } else {
                this.imageManager.startTransition(targetImage);
                this.uiController.updateStatus(`🔗 Navigating to: ${targetImage.name}`, 'success');
            }
        };
        
        // Gallery manager callbacks
        this.galleryManager.onImageSelected = (index) => {
            if (index !== this.imageManager.currentImageIndex) {
                this.imageManager.switchToImage(index);
            }
        };
    }

    /**
     * Setup UI event listeners
     */
    setupUIEventListeners() {
        this.uiController.setupEventListeners({
            onImageSelected: (e) => this.handleImageSelection(e),
            onTripodMaskSelected: (e) => this.handleTripodMaskSelection(e),
            onCustomImageSelected: (e) => this.handleCustomImageSelection(e),
            onTransitionDurationChanged: (duration) => {
                this.imageManager.setTransitionDuration(duration);
                this.markAsModified();
            },
            onTripodMaskSizeChanged: (size) => {
                this.tripodMaskManager.setSize(size);
                this.markAsModified();
            },
            onOpenCustomImageModal: () => this.openCustomImageModal(),
            onPlaceCustomImage: () => this.startHotspotPlacement(),
            onCustomImageSizeSelected: (size) => this.selectedCustomImageSize = size,
            onOpenConnectionModal: () => this.openConnectionModal(),
            onPlaceConnection: () => this.startConnectionPlacement(),
            onCancelConnection: () => this.cancelConnection(),
            onConnectionImageSelected: (index) => this.handleConnectionImageSelection(index)
        });
    }

    /**
     * Animation frame callback
     */
    onFrame() {
        // Update FPS
        this.uiController.updateFPS();
        
        // Update image transition
        this.imageManager.updateTransition(this.sceneManager.scene);
        
        // Animate navigation markers
        this.navigationManager.animateMarkers();
    }

    /**
     * Handle canvas click (for navigation markers)
     */
    handleCanvasClick(event) {
        this.navigationManager.handleMarkerClick(
            event,
            this.hotspotManager.isInPlacementMode()
        );
    }

    /**
     * Handle image file selection
     */
    async handleImageSelection(event) {
        const file = event.target.files[0];
        if (!file) return;
        
        this.uiController.showLoading(`Loading ${file.name}...`);
        
        try {
            const initialRotation = this.uiController.getInitialRotation();
            await this.imageManager.addImage(file, initialRotation);

            // Mark as modified after image is added
            this.markAsModified();

            this.uiController.showViewer();
            this.uiController.updateStatus(
                `✅ ${file.name} loaded${initialRotation ? ` with rotation ${initialRotation}°` : ''}`,
                'success'
            );
        } catch (error) {
            this.uiController.updateStatus(`❌ Error loading image: ${error.message}`, 'error');
            console.error('❌ Error loading image:', error);
        }
        
        this.uiController.hideLoading();
    }

    /**
     * Handle tripod mask file selection
     */
    async handleTripodMaskSelection(event) {
        const file = event.target.files[0];
        if (!file) return;
        
        this.uiController.showLoading(`Loading tripod mask ${file.name}...`);
        
        try {
            await this.tripodMaskManager.loadMask(file);
            this.markAsModified(); // Mark as modified after tripod mask is loaded
            this.uiController.updateStatus(`✅ Tripod mask loaded: ${file.name}`, 'success');
        } catch (error) {
            this.uiController.updateStatus(`❌ Error loading tripod mask: ${error.message}`, 'error');
            console.error('❌ Error loading tripod mask:', error);
        }
        
        this.uiController.hideLoading();
    }

    /**
     * Handle custom image file selection
     */
    async handleCustomImageSelection(event) {
        const file = event.target.files[0];
        if (!file) return;
        
        try {
            const url = URL.createObjectURL(file);
            await this.hotspotManager.prepareHotspot(file, this.selectedCustomImageSize);
            
            this.uiController.showCustomImagePreview(url);
            console.log('✅ Custom image prepared:', file.name);
        } catch (error) {
            this.uiController.updateStatus(`❌ Error loading custom image: ${error.message}`, 'error');
            console.error('❌ Error loading custom image:', error);
        }
    }

    /**
     * Open custom image modal
     */
    openCustomImageModal() {
        this.uiController.openCustomImageModal();
        this.selectedCustomImageSize = 'medium';
    }

    /**
     * Start hotspot placement
     */
    startHotspotPlacement() {
        this.uiController.closeCustomImageModal();
        
        // Set current image index for hotspot association
        this.hotspotManager.setCurrentImageIndex(this.imageManager.currentImageIndex);
        
        const raycastTargets = this.sphereManager.getRaycastObjects();
        this.hotspotManager.startPlacement(
            raycastTargets,
            (cursor) => this.sceneManager.setCursor(cursor)
        );
        
        this.uiController.updateStatus('🎯 Navigate to position and click Confirm to place the image (Esc to cancel)', 'success');
    }

    /**
     * Open connection modal
     */
    openConnectionModal() {
        const images = this.imageManager.getAllImages();
        
        if (images.length < 2) {
            this.uiController.updateStatus('❌ At least 2 images required to create a connection', 'error');
            return;
        }
        
        this.uiController.openConnectionModal();
        this.uiController.populateConnectionImageSelect(images, this.imageManager.currentImageIndex);
    }

    /**
     * Handle connection image selection
     */
    handleConnectionImageSelection(index) {
        if (index >= 0 && index < this.imageManager.getAllImages().length) {
            this.selectedConnectionEndImage = this.imageManager.getImage(index);
            this.uiController.updateConnectionStatus('Destination image selected. Click to place start point.');
            this.uiController.setPlaceConnectionEnabled(true);
        }
    }

    /**
     * Start connection placement
     */
    startConnectionPlacement() {
        if (!this.selectedConnectionEndImage) {
            this.uiController.updateStatus('❌ Please select a destination image', 'error');
            return;
        }
        
        const currentImage = this.imageManager.getCurrentImage();
        const raycastTargets = this.sphereManager.getRaycastObjects();
        
        this.navigationManager.startCreation(
            currentImage,
            this.selectedConnectionEndImage,
            raycastTargets,
            (cursor) => this.sceneManager.setCursor(cursor)
        );
        
        this.uiController.closeConnectionModal();
        this.uiController.updateStatus('🎯 Navigate to position and click Confirm to place the start point', 'success');
    }

    /**
     * Cancel connection creation
     */
    cancelConnection() {
        // Cancel navigation manager creation if active
        if (this.navigationManager.isCreatingConnection) {
            this.navigationManager.cancelCreation((cursor) => this.sceneManager.setCursor(cursor));
        }

        // Cancel graph connection mode if active (also resets the button via callback)
        if (this.graphManager && this.graphManager.isInConnectionMode) {
            this.graphManager.cancelConnectionMode();
        }

        // Hide viewer overlay
        if (this.viewerOverlay) {
            this.viewerOverlay.hide();
        }

        // Restore sidebar to normal size
        if (this.sandboxPanel) {
            this.sandboxPanel.restore();
        }

        this.uiController.updateStatus('❌ Création de connexion annulée', 'error');
    }

    /**
     * Handle custom image file selection
     */
    handleCustomImageSelected(e) {
        const file = e.target.files[0];
        if (!file) return;

        // Reset file input to allow selecting the same file again
        e.target.value = '';

        // Store filename for later use
        this.currentCustomImageName = file.name;

        // Read the image file
        const reader = new FileReader();
        reader.onload = (evt) => {
            const imageUrl = evt.target.result;
            // Start placement using HotspotManager pattern
            this.startCustomImagePlacement(imageUrl, file.name);
        };
        reader.readAsDataURL(file);
    }

    /**
     * Start custom image placement mode
     */
    async startCustomImagePlacement(imageUrl, imageName) {
        const currentImage = this.imageManager.getCurrentImage();
        if (!currentImage) {
            this.uiController.updateStatus('❌ No image selected', 'error');
            return;
        }

        // Minimize sidebar during placement
        if (this.sandboxPanel) {
            this.sandboxPanel.minimize();
        }

        // Load the texture
        const texture = await this.textureLoader.loadStandardTexture(imageUrl);
        this.hotspotManager.currentTexture = texture;
        this.hotspotManager.currentSize = 'medium';
        this.hotspotManager.currentImageName = imageName; // Store filename

        // Set the current image index and ID
        this.hotspotManager.setCurrentImageIndex(this.imageManager.currentImageIndex);
        this.hotspotManager.setCurrentImageId(currentImage.id);

        // Set up callbacks
        this.hotspotManager.onHotspotPlaced = () => {
            console.log('✅ Custom image placed');
            this.uiController.updateStatus(`✅ Image "${imageName}" placée avec succès`, 'success');

            // Mark as modified
            this.markAsModified();

            // Update sidebar list
            this.updateCustomImagesDisplay();

            // Restore sidebar to normal size
            if (this.sandboxPanel) {
                this.sandboxPanel.restore();
            }
        };

        this.hotspotManager.onPlacementCancelled = () => {
            this.uiController.updateStatus('❌ Placement annulé', 'error');

            // Restore sidebar to normal size
            if (this.sandboxPanel) {
                this.sandboxPanel.restore();
            }
        };

        const raycastTargets = this.sphereManager.getRaycastObjects();

        // Start placement with custom help text
        this.hotspotManager.startPlacement(
            raycastTargets,
            (cursor) => this.sceneManager.setCursor(cursor),
            'Placez l\'emplacement de l\'image'
        );

        this.uiController.updateStatus('🎯 Placez l\'image dans la scène', 'success');
    }

    /**
     * Update custom images display in sidebar
     */
    updateCustomImagesDisplay() {
        const listElement = document.getElementById('custom-images-list');
        if (!listElement) return;

        const currentImage = this.imageManager.getCurrentImage();
        if (!currentImage) {
            DOMSanitizer.setContent(listElement,
                DOMSanitizer.createElement('div', {
                    className: 'text-sm text-slate-400 text-center py-4',
                    textContent: 'Aucune image sélectionnée'
                })
            );
            return;
        }

        // Get all custom images (hotspots) for current image
        const customImages = this.hotspotManager.hotspots.filter(h => h.imageId === currentImage.id);

        if (customImages.length === 0) {
            DOMSanitizer.setContent(listElement,
                DOMSanitizer.createElement('div', {
                    className: 'text-sm text-slate-400 text-center py-4',
                    textContent: 'Aucune image personnalisée'
                })
            );
            return;
        }

        // Display custom images
        const items = customImages.map((hotspot, index) => {
            return this.createCustomImageItem(hotspot, index);
        });

        DOMSanitizer.setContent(listElement, items);
    }

    /**
     * Create a custom image item element
     * @param {Object} hotspot - Hotspot data
     * @param {number} index - Item index
     * @returns {HTMLElement} Item element
     */
    createCustomImageItem(hotspot, index) {
        const displayName = hotspot.imageName || `Image ${index + 1}`;
        const imageSrc = hotspot.texture?.image?.src || '';

        const container = DOMSanitizer.createElement('div', {
            className: 'flex items-center gap-3 p-3 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors'
        });

        const img = DOMSanitizer.createElement('img', {
            className: 'w-12 h-12 object-cover rounded',
            attributes: {
                src: imageSrc,
                alt: displayName
            }
        });

        const textContainer = DOMSanitizer.createElement('div', {
            className: 'flex-1 min-w-0'
        });

        const nameLabel = DOMSanitizer.createElement('div', {
            className: 'text-sm font-medium text-white truncate',
            textContent: displayName,
            attributes: {
                title: displayName
            }
        });

        const deleteBtn = DOMSanitizer.createElement('button', {
            className: 'p-2 hover:bg-red-500/20 rounded transition-colors',
            attributes: {
                title: 'Supprimer'
            }
        });

        // Add SVG icon to delete button
        deleteBtn.innerHTML = `
            <svg class="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
        `;

        // Use proper event delegation instead of onclick
        this.addEventListener(deleteBtn, 'click', () => this.removeCustomImage(hotspot.id));

        textContainer.appendChild(nameLabel);
        container.append(img, textContainer, deleteBtn);

        return container;
    }

    /**
     * Remove a custom image
     */
    removeCustomImage(hotspotId) {
        const hotspot = this.hotspotManager.hotspots.find(h => h.id === hotspotId);
        if (!hotspot) return;

        const displayName = hotspot.imageName || 'cette image';

        // Show confirmation modal
        if (this.deleteConfirmModal) {
            this.deleteConfirmModal.show(displayName, () => {
                this.hotspotManager.removeHotspot(hotspotId);
                this.markAsModified(); // Mark as modified after deletion
                this.updateCustomImagesDisplay();
                this.uiController.updateStatus('✅ Image personnalisée supprimée', 'success');
            });
        } else {
            // Fallback if modal not available
            this.hotspotManager.removeHotspot(hotspotId);
            this.markAsModified(); // Mark as modified after deletion
            this.updateCustomImagesDisplay();
            this.uiController.updateStatus('✅ Image personnalisée supprimée', 'success');
        }
    }

    /**
     * Check if WebGL is supported
     */
    isWebGLSupported() {
        try {
            const canvas = document.createElement('canvas');
            return !!(window.WebGLRenderingContext && 
                     (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
        } catch (e) {
            return false;
        }
    }

    /**
     * Show error message
     */
    showError(message) {
        if (this.uiController) {
            this.uiController.updateStatus(message, 'error');
        } else {
            console.error(message);
        }
    }

    /**
     * Apply mode-specific configuration
     */
    applyModeConfiguration() {
        console.log(`🔧 Applying ${this.mode} mode configuration...`);

        if (this.mode === 'view') {
            // In view mode, hide editing controls and unnecessary UI elements
            const editButtons = [
                'add-new-image',
                'add-custom-image',
                'create-connection',
                'load-tripod-mask'
            ];

            editButtons.forEach(id => {
                const element = document.getElementById(id);
                if (element) {
                    element.style.display = 'none';
                }
            });

            // Hide entire accordions that are not needed in read-only mode
            const accordionsToHide = [
                'images-content',          // Hide entire images accordion (parent will be hidden)
                'tripod-content',          // Hide tripod mask accordion content
                'transitions-content',     // Hide transitions accordion content
                'rotation-content'         // Hide initial rotation accordion content
            ];

            accordionsToHide.forEach(id => {
                const element = document.getElementById(id);
                if (element && element.parentElement) {
                    // Hide the entire accordion item (parent of the content)
                    element.parentElement.style.display = 'none';
                }
            });

            // Hide status bar and FPS counter
            const statusBar = document.getElementById('status-bar');
            if (statusBar) {
                statusBar.style.display = 'none';
            }

            const fpsCounter = document.getElementById('fps-counter');
            if (fpsCounter) {
                fpsCounter.style.display = 'none';
            }

            console.log('✅ View mode: Editing controls and unnecessary UI hidden');
        } else {
            console.log('✅ Edit mode: All controls enabled');
        }
    }

    /**
     * Load data from API (readonly mode)
     * @param {Object} apiData - Raw API response data
     */
    async loadApiData(apiData) {
        const loadStartTime = performance.now();
        console.log('[App] Loading API data...');

        try {
            // Parse API data (DataLoader will extract galleryId and sceneId)
            const parsedData = this.dataLoader.parseApiData(apiData);

            // Store galleryId and sceneId from parsed data
            if (parsedData.galleryId) {
                this.galleryId = parsedData.galleryId;
                console.log(`[App] 📌 Gallery ID loaded: ${this.galleryId}`);
            }
            if (parsedData.sceneId) {
                this.sceneId = parsedData.sceneId;
                console.log(`[App] 📌 Scene ID loaded: ${this.sceneId}`);
            }
            if (parsedData.galleryPath) {
                console.log(`[App] 📌 Gallery Path: ${parsedData.galleryPath}`);
            }

            // Load project title/name if available
            if (parsedData.title) {
                this.projectTitle = parsedData.title;
                console.log(`[App] 📌 Project title loaded: ${parsedData.title}`);
            }

            if (!parsedData.pictures || parsedData.pictures.length === 0) {
                throw new Error('No pictures found in API data');
            }

            // Show loading overlay
            this.uiController.showLoading(`Chargement de votre visite virtuelle...`);

            // Load all images with progress tracking
            const results = await this.dataLoader.loadImages(
                parsedData.pictures,
                (current, total) => {
                    this.uiController.updateLoadingText(`Chargement des images: ${current}/${total}`);
                }
            );

            // Filter successful results
            const successfulResults = results.filter(r => r.success);

            if (successfulResults.length === 0) {
                throw new Error('Failed to load any images');
            }

            console.log(`[App] Successfully loaded ${successfulResults.length} images`);

            // Convert all images to files in parallel (faster - skip blob conversion)
            this.uiController.updateLoadingText('Préparation de l\'affichage...');

            const imageDataList = successfulResults.map((result, index) => {
                const { picture, image } = result;

                // Use the image URL directly instead of converting to blob
                // The browser has already cached the image
                const file = this.createFileFromImage(image, picture.filename, picture.url);

                return {
                    picture,
                    file,
                    index
                };
            });

            console.log(`[App] Prepared ${imageDataList.length} files`);

            // Preload ALL textures in parallel for instant transitions
            this.uiController.updateLoadingText('Optimisation de l\'expérience...');

            const texturePromises = imageDataList.map(async ({ file, picture }) => {
                try {
                    const url = file.url;
                    const texture = await this.textureLoader.load360Texture(url);
                    console.log(`[App] Texture preloaded: ${picture.name}`);
                    return {
                        picture,
                        file,
                        texture,
                        url,
                        success: true
                    };
                } catch (error) {
                    console.error(`[App] Failed to preload texture for ${picture.name}:`, error);
                    return {
                        picture,
                        file,
                        texture: null,
                        url: file.url,
                        success: false
                    };
                }
            });

            const preloadedTextures = await Promise.all(texturePromises);
            const successfulTextures = preloadedTextures.filter(t => t.success);

            console.log(`[App] Preloaded ${successfulTextures.length}/${imageDataList.length} textures`);

            // Add all images with preloaded textures (instant, no loading lag)
            this.uiController.updateLoadingText('Finalisation du chargement...');

            for (let i = 0; i < successfulTextures.length; i++) {
                const { picture, file, texture, url } = successfulTextures[i];

                // Add image directly with preloaded texture (no loading needed)
                // Use initialRotation from API data (or default to 0)
                const initialRotation = picture.initialRotation || 0;
                const thumbnailUrl = picture.thumbnail_url || null;

                await this.imageManager.addImageWithTexture(file, texture, url, initialRotation, true, thumbnailUrl);

                // Store the image index
                const imageIndex = this.imageManager.currentImageIndex;
                const imageData = this.imageManager.getImage(imageIndex);

                if (thumbnailUrl) {
                    console.log(`[App] 📸 Added thumbnail_url for ${picture.name}: ${thumbnailUrl}`);
                }

                // Apply brightness from API data (if available)
                if (picture.brightness) {
                    imageData.brightness = picture.brightness;
                }

                // Store mapping between picture ID and image data (for connections)
                this.imageManager.setPictureIdMapping(picture.id, imageData);
                console.log(`[App] 🔗 Mapped picture ID ${picture.id} to image ${imageData.name} (image.id: ${imageData.id})`);

                // Add UI button
                this.uiController.addImageButton(
                    { name: picture.name, file: file },
                    imageIndex,
                    i === 0, // Only mark first as active
                    () => this.imageManager.switchToImage(imageIndex)
                );

                // Add node to graph (sandbox mode only) - manually since images are added in silent mode
                if (this.graphManager) {
                    const label = imageData.alias || imageData.name;
                    // Only use thumbnail_url (no fallback to url as it may be KTX2)
                    const thumbnailUrl = imageData.thumbnail_url;
                    console.log(`[App] 🖼️ Adding graph node for "${label}":`, {
                        hasThumbnail: !!imageData.thumbnail_url,
                        thumbnailUrl: thumbnailUrl,
                        imageUrl: imageData.url
                    });
                    this.graphManager.addNode(imageData.id, label, null, null, thumbnailUrl);
                }
            }

            // Set selected image in graph (first image) and update graph info
            if (this.graphManager && this.imageManager.images.length > 0) {
                const firstImage = this.imageManager.getImage(0);
                if (firstImage) {
                    this.graphManager.setSelectedImage(firstImage.id);
                }
                // Update graph info to reflect the added nodes
                this.updateGraphInfo();
            }

            // Pre-create transition sphere for smooth transitions
            this.imageManager.initializeTransitionSphere(this.sceneManager.scene);

            // Apply the first image's texture manually (since images were added in silent mode)
            if (successfulTextures.length > 0) {
                console.log('[App] Displaying first image');
                const firstImage = this.imageManager.getImage(0);
                if (firstImage && firstImage.texture) {
                    console.log('[App] Applying first texture to sphere:', firstImage.name);
                    this.sphereManager.setTexture(firstImage.texture);

                    // Apply initial rotation if specified
                    const { picture } = successfulTextures[0];
                    if (picture.initialRotation && this.sceneManager) {
                        const rotationRadians = (picture.initialRotation * Math.PI) / 180;
                        this.sceneManager.setCameraRotation(rotationRadians);
                        console.log(`[App] Applied initial rotation: ${picture.initialRotation}°`);
                    }
                }
            }

            // Initialize gallery
            this.galleryManager.initialize(
                document.getElementById('gallery-panel'),
                document.getElementById('gallery-thumbnails'),
                document.getElementById('gallery-toggle-btn')
            );

            const t0 = performance.now();
            await this.galleryManager.waitForThumbnails();
            const t1 = performance.now();
            console.log(`[PERF] Wait for all thumbnails: ${(t1 - t0).toFixed(2)}ms`);

            this.galleryManager.updateThumbnails();

            // Show viewer controls but keep canvas and loading overlay
            this.uiController.showViewer();

            // GPU warmup with progress tracking
            this.uiController.updateLoadingText('Préparation de l\'expérience immersive...');
            await this.warmupGPUTextures(successfulTextures, (current, total) => {
                this.uiController.updateLoadingText(`Préparation: ${current}/${total}`);
            });

            const loadEndTime = performance.now();
            const totalLoadTime = loadEndTime - loadStartTime;
            console.log(`[App] ⚡ Total loading time: ${totalLoadTime.toFixed(0)}ms (${(totalLoadTime / 1000).toFixed(2)}s)`);

            this.uiController.updateStatus(
                `✅ Loaded ${successfulResults.length} images from API`,
                'success'
            );

            // Load connections if available
            if (parsedData.graph && parsedData.graph.connections && parsedData.graph.connections.length > 0) {
                console.log(`[App] Loading ${parsedData.graph.connections.length} connections from API data...`);

                this.navigationManager.loadConnectionsFromAPI(
                    parsedData.graph.connections,
                    (pictureId) => this.imageManager.getImageByPictureId(pictureId)
                );

                // Add edges to graph for loaded connections (sandbox mode only)
                if (this.graphManager) {
                    this.navigationManager.connections.forEach(connection => {
                        const startNode = this.graphManager.nodes.find(n => n.imageId === connection.startImage.id);
                        const endNode = this.graphManager.nodes.find(n => n.imageId === connection.endImage.id);

                        if (startNode && endNode) {
                            this.graphManager.addEdge(startNode.id, endNode.id);
                        }
                    });
                    this.updateGraphInfo();
                }

                this.uiController.updateStatus(
                    `✅ Loaded ${parsedData.graph.connections.length} navigation connections`,
                    'success'
                );
            }

            // Hide loading - viewer is ready with graph displayed!
            // Logo, tripod mask and hotspots will load in background without blocking UI
            this.uiController.hideLoading();

            // Load logo overlay if available
            if (parsedData.graph && parsedData.graph.logoOverlay) {
                await this.logoOverlayManager.loadData(parsedData.graph.logoOverlay);
                console.log('[App] ✅ Logo overlay loaded');
            }

            // Load tripod mask if available
            if (parsedData.graph && parsedData.graph.tripodMasks && parsedData.graph.tripodMasks.enabled) {
                const tripodData = parsedData.graph.tripodMasks;
                if (tripodData.imageUrl) {
                    console.log('[App] Loading tripod mask from API...');
                    try {
                        // Load the mask from URL with cache-busting version if available
                        await this.tripodMaskManager.loadMaskFromURL(
                            tripodData.imageUrl,
                            tripodData.version || null
                        );

                        // Set the size if specified
                        if (tripodData.size) {
                            this.tripodMaskManager.setSize(tripodData.size);
                        }

                        console.log(`[App] ✅ Tripod mask loaded with size ${tripodData.size || 1.0}${tripodData.version ? ` (v${tripodData.version})` : ''}`);
                    } catch (error) {
                        console.error('[App] Failed to load tripod mask:', error);
                    }
                }
            }

            // Load custom images (hotspots) if available
            if (parsedData.graph && parsedData.graph.customImages && parsedData.graph.customImages.length > 0) {
                console.log(`[App] Loading ${parsedData.graph.customImages.length} custom images (hotspots)...`);
                await this.hotspotManager.loadHotspotsFromAPI(
                    parsedData.graph.customImages,
                    this.imageManager
                );
                this.uiController.updateStatus(
                    `✅ Loaded ${parsedData.graph.customImages.length} custom images`,
                    'success'
                );
            }

            // Show markers/hotspots for the initial image (since no transition was triggered)
            // IMPORTANT: After loading, currentImageIndex is set to the last loaded image
            // but the displayed image is the first one (index 0), so we need to fix this
            this.imageManager.currentImageIndex = 0;

            const firstImage = this.imageManager.getImage(0);
            if (firstImage) {
                this.hotspotManager.showForImage(0);
                this.navigationManager.showForImage(firstImage);
                console.log('[App] ✅ Markers/hotspots shown for initial image (index 0):', firstImage.name);
            }

        } catch (error) {
            console.error('[App] Error loading API data:', error);
            this.uiController.updateStatus(`❌ Error loading data: ${error.message}`, 'error');
            this.uiController.hideLoading(); // Hide loading on error
            throw error;
        }
    }

    /**
     * Create a File object from an Image element or URL
     * Uses a lightweight approach by creating a fetch from the cached image
     * @param {HTMLImageElement|string} image - Loaded image or URL (for KTX2)
     * @param {string} filename - File name
     * @param {string} url - Image URL
     * @returns {File} File object
     */
    createFileFromImage(image, filename, url) {
        // Detect file type from URL
        const isKTX2 = url.toLowerCase().endsWith('.ktx2');
        const fileType = isKTX2 ? 'image/ktx2' : 'image/jpeg';

        // Create a minimal File-like object
        // The image is already in browser cache (or URL for KTX2), we just need a reference
        const file = {
            name: filename,
            type: fileType,
            url: url,
            _cachedImage: typeof image === 'string' ? null : image, // Keep reference to cached image (null for KTX2)
            _isKTX2: isKTX2 // Flag to identify KTX2 files
        };

        // Add File-like properties for compatibility
        Object.defineProperty(file, 'toString', {
            value: () => `[File: ${filename}]`
        });

        return file;
    }

    /**
     * Warm up GPU by forcing all textures to be uploaded to VRAM
     * This prevents the first-time lag when switching to a new image
     * @param {Array} textureList - List of preloaded textures
     * @param {Function} onProgress - Progress callback (current, total)
     */
    async warmupGPUTextures(textureList, onProgress = null) {
        const startTime = performance.now();

        const renderer = this.sceneManager.renderer;
        const gl = renderer.getContext();

        console.log(`[App] 🔥 Starting GPU warmup for ${textureList.length} textures...`);

        // Skip the first texture (already displayed)
        for (let i = 1; i < textureList.length; i++) {
            const { texture } = textureList[i];

            // Just initiate the texture on the renderer (forces GPU upload without full render)
            renderer.initTexture(texture);

            // Force WebGL to finish uploading this texture
            gl.finish();

            // Report progress
            if (onProgress) {
                onProgress(i, textureList.length - 1);
            }
        }

        const endTime = performance.now();
        const avgTimePerTexture = (endTime - startTime) / (textureList.length - 1);
        console.log(`[App] ✅ GPU warmup complete: ${(endTime - startTime).toFixed(0)}ms for ${textureList.length} textures (~${avgTimePerTexture.toFixed(0)}ms/texture)`);
    }

    /**
     * Setup graph manager callbacks
     */
    setupGraphCallbacks() {
        if (!this.graphManager) return;

        // Node click - switch to that image
        this.graphManager.onNodeClick = (node) => {
            console.log(`🗺️ Graph node clicked: ${node.label}`);
            const imageIndex = this.imageManager.images.findIndex(img => img.id === node.imageId);
            if (imageIndex !== -1 && imageIndex !== this.imageManager.currentImageIndex) {
                this.imageManager.switchToImage(imageIndex);
            }
        };

        // Node right-click - show context menu
        this.graphManager.onNodeRightClick = (node, event) => {
            this.showNodeContextMenu(node, event);
        };

        // Edge right-click - show edge context menu
        this.graphManager.onEdgeRightClick = (edge, event) => {
            this.showEdgeContextMenu(edge, event);
        };

        // Graph changed - update info display
        this.graphManager.onGraphChanged = () => {
            this.updateGraphInfo();
        };

        // Zoom buttons
        const zoomInBtn = document.getElementById('graph-zoom-in');
        if (zoomInBtn) {
            this.addEventListener(zoomInBtn, 'click', () => {
                this.graphManager.zoomIn();
            });
        }

        const zoomOutBtn = document.getElementById('graph-zoom-out');
        if (zoomOutBtn) {
            this.addEventListener(zoomOutBtn, 'click', () => {
                this.graphManager.zoomOut();
            });
        }

        const resetViewBtn = document.getElementById('graph-reset-view');
        if (resetViewBtn) {
            this.addEventListener(resetViewBtn, 'click', () => {
                this.graphManager.resetView();
            });
        }

        // Add image button
        const graphAddImageBtn = document.getElementById('graph-add-image');
        if (graphAddImageBtn) {
            this.addEventListener(graphAddImageBtn, 'click', () => {
                document.getElementById('file-input')?.click();
            });
        }

        // Create connection button
        const graphCreateConnectionBtn = document.getElementById('graph-create-connection');
        if (graphCreateConnectionBtn) {
            this.addEventListener(graphCreateConnectionBtn, 'click', () => {
                const btn = document.getElementById('graph-create-connection');
                if (this.graphManager.isInConnectionMode) {
                    // Cancel mode
                    this.cancelConnection();
                } else {
                    // Start mode
                    this.startGraphConnectionMode();
                }
            });
        }

        // Connection requested callback (when user selects destination in graph)
        this.graphManager.onConnectionRequested = (sourceNode, destNode) => {
            this.handleGraphConnectionRequest(sourceNode, destNode);
        };

        // Connection mode changed callback
        this.graphManager.onConnectionModeChanged = (isActive) => {
            this.updateConnectionButton(isActive);
        };

        console.log('✅ Graph manager callbacks setup');
    }

    /**
     * Show context menu for graph node
     */
    showNodeContextMenu(node, event) {
        if (!this.contextMenu) return;

        // Show context menu at mouse position
        this.contextMenu.show(
            event.clientX,
            event.clientY,
            // On rename
            () => {
                this.renameModal.show(node.label, (newName) => {
                    node.label = newName;
                    this.graphManager.render();

                    // Update image alias
                    const image = this.imageManager.images.find(img => img.id === node.imageId);
                    if (image) {
                        image.alias = newName;
                    }

                    // Mark as modified
                    this.markAsModified();

                    this.uiController.updateStatus('✅ Image renommée', 'success');
                });
            },
            // On delete
            () => {
                this.deleteConfirmModal.show(node.label, () => {
                    // Remove from graph
                    this.graphManager.removeNode(node.id);

                    // Remove from image manager
                    const imageIndex = this.imageManager.images.findIndex(img => img.id === node.imageId);
                    if (imageIndex !== -1) {
                        this.imageManager.images.splice(imageIndex, 1);

                        // Switch to first image if current was deleted
                        if (this.imageManager.currentImageIndex === imageIndex && this.imageManager.images.length > 0) {
                            this.imageManager.switchToImage(0);
                        }
                    }

                    // Mark as modified
                    this.markAsModified();

                    this.updateGraphInfo();
                    this.uiController.updateStatus('✅ Image supprimée', 'success');
                });
            }
        );
    }

    /**
     * Show context menu for graph edge (connection)
     */
    showEdgeContextMenu(edge, event) {
        if (!this.edgeContextMenu) return;

        // Get node labels for display
        const fromNode = this.graphManager.nodes.find(n => n.id === edge.from);
        const toNode = this.graphManager.nodes.find(n => n.id === edge.to);

        if (!fromNode || !toNode) return;

        console.log(`[App] Edge context menu: ${fromNode.label} <-> ${toNode.label}`);

        // Show edge context menu at mouse position
        this.edgeContextMenu.show(
            event.clientX,
            event.clientY,
            // On delete
            () => {
                console.log(`[App] Deleting edge between ${fromNode.label} and ${toNode.label}`);

                // Remove the edge from graph
                this.graphManager.removeEdge(edge.from, edge.to);

                // Also remove from navigation manager
                if (this.navigationManager) {
                    this.navigationManager.removeConnection(fromNode.imageId, toNode.imageId);
                }

                // Mark as modified
                this.markAsModified();

                this.updateGraphInfo();
                this.toast.success('Connexion supprimée', 2500);
            }
        );
    }

    /**
     * Update graph info display
     */
    updateGraphInfo() {
        if (!this.sandboxPanel || !this.graphManager) return;

        const imageCount = this.graphManager.nodes.length;
        const connectionCount = this.navigationManager.connections.length;

        this.sandboxPanel.updateGraphInfo(imageCount, connectionCount);
        this.updateConnectionButtonState();
    }

    /**
     * Update connection button state based on number of images
     */
    updateConnectionButtonState() {
        const connectionBtn = document.getElementById('graph-create-connection');
        const tooltip = document.getElementById('graph-connection-tooltip');

        if (!connectionBtn) return;

        const imageCount = this.graphManager?.nodes.length || 0;
        const currentImage = this.imageManager?.getCurrentImage();

        // Check if we have at least 2 images
        if (imageCount < 2) {
            connectionBtn.disabled = true;
            if (tooltip) {
                tooltip.textContent = 'Au moins 2 images sont nécessaires';
                tooltip.classList.remove('hidden');
            }
        }
        // Check if current image has available connections
        else if (currentImage && !this.graphManager.hasAvailableConnections(currentImage.id)) {
            connectionBtn.disabled = true;
            if (tooltip) {
                tooltip.textContent = 'Toutes les connexions possibles existent déjà';
                tooltip.classList.remove('hidden');
            }
        }
        else {
            connectionBtn.disabled = false;
            if (tooltip) {
                tooltip.classList.add('hidden');
            }
        }
    }

    /**
     * Update connection button appearance based on mode
     */
    updateConnectionButton(isInConnectionMode) {
        const connectionBtn = document.getElementById('graph-create-connection');
        if (!connectionBtn) return;

        if (isInConnectionMode) {
            // Change to cancel mode
            connectionBtn.textContent = '❌ Annuler la sélection';
            connectionBtn.className = 'w-full px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white text-sm font-medium rounded-lg transition-all duration-300';
        } else {
            // Change to create mode
            connectionBtn.textContent = '🔗 Créer une connexion';
            connectionBtn.className = 'w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-600 disabled:to-gray-700';
        }
    }

    /**
     * Setup sandbox panel callbacks
     */
    setupSandboxCallbacks() {
        if (!this.sandboxPanel) return;

        // Save button callback
        this.sandboxPanel.onSave = async () => {
            console.log('💾 Save requested - showing project name modal...');

            // Show modal to get/edit project name before saving
            this.saveModal.show(
                this.projectTitle,
                async (projectTitle) => {
                    // User confirmed with project title - proceed with save
                    this.projectTitle = projectTitle;
                    console.log(`💾 Project title set to: "${projectTitle}" - building JSON...`);

                    try {
                        // STEP 1: Show preparing step
                        this.saveProgressOverlay.showPreparingStep();

                        // Build JSON data
                        const data = await this.buildVirtualTourJSON();
                        console.log('Generated JSON:', data);

                        // Count images for progress tracking
                        const imageCount = data.pictures?.length || 0;

                        // STEP 2: Show optimizing step (initial state)
                        this.saveProgressOverlay.showOptimizingStep(0, imageCount);

                        // Simulate progress updates (1.5-2s per image)
                        // In a real implementation, the backend would send progress updates
                        let imagesProcessed = 0;
                        const progressInterval = setInterval(() => {
                            if (imagesProcessed < imageCount) {
                                imagesProcessed++;
                                this.saveProgressOverlay.showOptimizingStep(imagesProcessed, imageCount);
                            }
                        }, 1750); // ~1.75s per image average

                        // Call API to save
                        // Use galleryUuid if we already have one (update), otherwise create new (no galleryUuid param)
                        const response = await this.apiService.saveVirtualTour(data, this.galleryUuid);
                        console.log('API Response:', response);

                        // Track if this is the first save (no galleryUuid before)
                        const isFirstSave = !this.galleryUuid;

                        // Store galleryId, sceneId, and uuid from the response for future updates
                        if (response.galleryId) {
                            this.galleryId = response.galleryId;
                            console.log(`[App] 📌 Gallery ID (numeric) stored: ${this.galleryId}`);
                        }
                        if (response.sceneId) {
                            this.sceneId = response.sceneId;
                            console.log(`[App] 📌 Scene ID stored: ${this.sceneId} (CDN path: gallery_360/${this.sceneId})`);
                        }

                        // Store UUID from response for future updates
                        if (response.uuid || response.id) {
                            this.galleryUuid = response.uuid || response.id;
                            console.log(`[App] 📌 Gallery UUID stored: ${this.galleryUuid}`);
                        }

                        // Auto-attach to project if enabled and this is the first save
                        console.log('[App] 🔍 Checking auto-attach conditions:', {
                            autoAttach: this.autoAttach,
                            projectId: this.projectId,
                            isFirstSave: isFirstSave,
                            hasBeenAttached: this.hasBeenAttached,
                            galleryUuid: this.galleryUuid,
                            willAttach: this.autoAttach && this.projectId && isFirstSave && !this.hasBeenAttached && this.galleryUuid
                        });

                        if (this.autoAttach && this.projectId && isFirstSave && !this.hasBeenAttached && this.galleryUuid) {
                            try {
                                console.log(`[App] 🔗 Auto-attaching gallery ${this.galleryUuid} to project ${this.projectId}...`);
                                await this.apiService.attachToProject(this.galleryUuid, this.projectId);
                                this.hasBeenAttached = true;
                                console.log('[App] ✅ Gallery successfully attached to project');
                            } catch (attachError) {
                                console.error('[App] ⚠️ Failed to attach gallery to project:', attachError);
                                // Don't fail the save if attach fails - just log the error
                            }
                        } else {
                            console.log('[App] ⏭️ Auto-attach skipped (conditions not met)');
                        }

                        // Clear the progress interval
                        clearInterval(progressInterval);

                        // STEP 3: Show completion
                        this.saveProgressOverlay.showCompleteStep();

                        // Show success status
                        const saveType = this.galleryId ? 'mise à jour' : 'sauvegarde';
                        this.uiController.updateStatus(`✅ Visite virtuelle ${saveType} avec succès`, 'success');

                        // Hide overlay after 1.5 seconds
                        setTimeout(() => {
                            this.saveProgressOverlay.hide();
                        }, 1500);

                    } catch (error) {
                        console.error('Save error:', error);

                        // Show error in overlay
                        const errorMessage = error.message || 'Erreur inconnue';
                        this.saveProgressOverlay.showErrorStep(`Erreur lors de la sauvegarde: ${errorMessage}`);

                        // Show error status
                        this.uiController.updateStatus(`❌ Erreur lors de la sauvegarde: ${errorMessage}`, 'error');

                        // Hide overlay after 3 seconds
                        setTimeout(() => {
                            this.saveProgressOverlay.hide();
                        }, 3000);
                    }
                },
                () => {
                    // User cancelled - do nothing
                    console.log('💾 Save cancelled by user');
                }
            );
        };

        // Graph tab activated callback - resize canvas
        this.sandboxPanel.onGraphTabActivated = () => {
            if (this.graphManager) {
                this.graphManager.resizeCanvas();
            }
        };

        // Custom image add button callback
        const customImageAddBtn = document.getElementById('custom-image-add');
        if (customImageAddBtn) {
            this.addEventListener(customImageAddBtn, 'click', () => {
                this.customImageInput.click();
            });
        }

        // Logo upload button callback
        const logoInput = document.createElement('input');
        logoInput.type = 'file';
        logoInput.accept = 'image/*';
        logoInput.style.display = 'none';
        document.body.appendChild(logoInput);

        const logoUploadBtn = document.getElementById('logo-upload-btn');
        if (logoUploadBtn) {
            this.addEventListener(logoUploadBtn, 'click', () => {
                logoInput.click();
            });
        }

        this.addEventListener(logoInput, 'change', async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                await this.logoOverlayManager.setLogo(file);

                // Show preview
                const previewContainer = document.getElementById('logo-preview-container');
                const previewImg = document.getElementById('logo-preview-img');
                if (previewContainer && previewImg) {
                    previewImg.src = this.logoOverlayManager.imageUrl;
                    previewContainer.classList.remove('hidden');
                }

                // Update button text
                const uploadBtnText = document.getElementById('logo-upload-btn-text');
                if (uploadBtnText) {
                    uploadBtnText.textContent = 'Remplacer le logo';
                }

                // Show position selector and delete button
                document.getElementById('logo-position-selector')?.classList.remove('hidden');
                document.getElementById('logo-delete-btn')?.classList.remove('hidden');

                this.uiController.updateStatus('✅ Logo ajouté', 'success');
            } catch (error) {
                console.error('Error loading logo:', error);
                this.uiController.updateStatus('❌ Erreur lors du chargement du logo', 'error');
            }

            // Reset input
            e.target.value = '';
        });

        // Logo position buttons callback
        document.querySelectorAll('.logo-position-btn').forEach(btn => {
            this.addEventListener(btn, 'click', () => {
                const position = btn.dataset.position;
                this.logoOverlayManager.setPosition(position);

                // Update button styles
                document.querySelectorAll('.logo-position-btn').forEach(b => {
                    b.classList.remove('bg-blue-600', 'ring-2', 'ring-blue-400');
                    b.classList.add('bg-slate-700');
                });
                btn.classList.remove('bg-slate-700');
                btn.classList.add('bg-blue-600', 'ring-2', 'ring-blue-400');

                this.uiController.updateStatus(`✅ Position du logo: ${position}`, 'success');
            });
        });

        // Logo delete button callback
        const logoDeleteBtn = document.getElementById('logo-delete-btn');
        if (logoDeleteBtn) {
            this.addEventListener(logoDeleteBtn, 'click', () => {
                if (this.deleteConfirmModal) {
                    this.deleteConfirmModal.show('le logo', () => {
                        this.logoOverlayManager.removeLogo();

                        // Hide preview, position selector, and delete button
                        document.getElementById('logo-preview-container')?.classList.add('hidden');
                        document.getElementById('logo-position-selector')?.classList.add('hidden');
                        document.getElementById('logo-delete-btn')?.classList.add('hidden');

                        // Reset button text
                        const uploadBtnText = document.getElementById('logo-upload-btn-text');
                        if (uploadBtnText) {
                            uploadBtnText.textContent = 'Charger un logo';
                        }

                        // Reset position button styles
                        document.querySelectorAll('.logo-position-btn').forEach(b => {
                            b.classList.remove('bg-blue-600', 'ring-2', 'ring-blue-400');
                            b.classList.add('bg-slate-700');
                        });

                        this.uiController.updateStatus('✅ Logo supprimé', 'success');
                    });
                }
            });
        }

        // Tripod mask upload button callback
        const tripodInput = document.createElement('input');
        tripodInput.type = 'file';
        tripodInput.accept = 'image/*';
        tripodInput.style.display = 'none';
        document.body.appendChild(tripodInput);

        const tripodLoadBtn = document.getElementById('tripod-load-btn');
        if (tripodLoadBtn) {
            this.addEventListener(tripodLoadBtn, 'click', () => {
                tripodInput.click();
            });
        }

        this.addEventListener(tripodInput, 'change', async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                await this.tripodMaskManager.loadMask(file);

                // Show preview
                const previewContainer = document.getElementById('tripod-preview-container');
                const previewImg = document.getElementById('tripod-preview-img');
                if (previewContainer && previewImg) {
                    previewImg.src = URL.createObjectURL(file);
                    previewContainer.classList.remove('hidden');
                }

                // Update button text
                const uploadBtnText = document.getElementById('tripod-load-btn-text');
                if (uploadBtnText) {
                    uploadBtnText.textContent = 'Remplacer le masque';
                }

                // Show size control and delete button
                document.getElementById('tripod-size-control')?.classList.remove('hidden');
                document.getElementById('tripod-delete-btn')?.classList.remove('hidden');

                this.uiController.updateStatus('✅ Masque trépied ajouté', 'success');
            } catch (error) {
                console.error('Error loading tripod mask:', error);
                this.uiController.updateStatus('❌ Erreur lors du chargement du masque', 'error');
            }

            // Reset input
            e.target.value = '';
        });

        // Tripod size slider callback
        const tripodSizeSlider = document.getElementById('tripod-size-slider');
        const tripodSizeDisplay = document.getElementById('tripod-size-display');

        if (tripodSizeSlider) {
            this.addEventListener(tripodSizeSlider, 'input', (e) => {
                const size = parseFloat(e.target.value);
                this.tripodMaskManager.setSize(size);
                if (tripodSizeDisplay) {
                    tripodSizeDisplay.textContent = `${size.toFixed(1)}x`;
                }
            });
        }

        // Tripod delete button callback
        const tripodDeleteBtn = document.getElementById('tripod-delete-btn');
        if (tripodDeleteBtn) {
            this.addEventListener(tripodDeleteBtn, 'click', () => {
                if (this.deleteConfirmModal) {
                    this.deleteConfirmModal.show('le masque trépied', () => {
                        this.tripodMaskManager.removeMask();

                        // Hide preview, size control, and delete button
                        document.getElementById('tripod-preview-container')?.classList.add('hidden');
                        document.getElementById('tripod-size-control')?.classList.add('hidden');
                        document.getElementById('tripod-delete-btn')?.classList.add('hidden');

                        // Reset button text
                        const uploadBtnText = document.getElementById('tripod-load-btn-text');
                        if (uploadBtnText) {
                            uploadBtnText.textContent = 'Charger masque trépied';
                        }

                        // Reset slider
                        if (tripodSizeSlider) {
                            tripodSizeSlider.value = '1.0';
                        }
                        if (tripodSizeDisplay) {
                            tripodSizeDisplay.textContent = '1.0x';
                        }

                        this.uiController.updateStatus('✅ Masque trépied supprimé', 'success');
                    });
                }
            });
        }

        console.log('✅ Sandbox panel callbacks setup');
    }

    /**
     * Setup image rotation and brightness correction tools
     */
    setupImageControls() {
        const toolsToggleBtn = document.getElementById('tools-toggle-btn');
        const toolsPanel = document.getElementById('tools-panel');
        const correctRotationBtn = document.getElementById('correct-rotation-btn');
        const correctBrightnessBtn = document.getElementById('correct-brightness-btn');

        const rotationPanel = document.getElementById('rotation-panel');
        const rotationSlider = document.getElementById('rotation-panel-slider');
        const rotationValue = document.getElementById('rotation-panel-value');
        const rotationConfirmBtn = document.getElementById('rotation-panel-confirm');
        const rotationCancelBtn = document.getElementById('rotation-panel-cancel');

        const brightnessPanel = document.getElementById('brightness-panel');
        const brightnessSlider = document.getElementById('brightness-panel-slider');
        const brightnessValue = document.getElementById('brightness-panel-value');
        const brightnessConfirmBtn = document.getElementById('brightness-panel-confirm');
        const brightnessCancelBtn = document.getElementById('brightness-panel-cancel');

        if (!toolsToggleBtn) return;

        // Show tools button
        toolsToggleBtn.classList.remove('hidden');

        // Toggle tools panel
        let toolsPanelOpen = false;
        if (toolsToggleBtn) {
            this.addEventListener(toolsToggleBtn, 'click', () => {
                toolsPanelOpen = !toolsPanelOpen;
                if (toolsPanelOpen) {
                    toolsPanel.classList.remove('hidden');
                } else {
                    toolsPanel.classList.add('hidden');
                }
            });
        }

        // Correct rotation button
        if (correctRotationBtn) {
            this.addEventListener(correctRotationBtn, 'click', () => {
            const currentImage = this.imageManager.getCurrentImage();
            if (!currentImage) return;

            // Hide tools panel
            toolsPanel.classList.add('hidden');
            toolsPanelOpen = false;

            // Disable controls during correction
            this.sceneManager.setControlsEnabled(false);

            // Enable cursor message for correction mode
            this.enableCorrectionCursorMessage();

            // Set slider to current rotation
            const currentRotation = currentImage.initialRotation || 0;
            rotationSlider.value = currentRotation.toString();
            rotationValue.textContent = `${currentRotation}°`;

            // Set camera to current rotation immediately (no animation)
            const radians = (currentRotation * Math.PI) / 180;
            this.sceneManager.setCameraRotation(radians);

            // Show panel and reduce sidebar
            rotationPanel.classList.remove('hidden');
            this.sandboxPanel?.minimize();
            });
        }

        // Rotation slider real-time update
        if (rotationSlider) {
            this.addEventListener(rotationSlider, 'input', (e) => {
                const degrees = parseInt(e.target.value);
                const radians = (degrees * Math.PI) / 180;

                // Apply rotation to camera, not sphere
                this.sceneManager.setCameraRotation(radians);

                rotationValue.textContent = `${degrees}°`;
            });
        }

        // Rotation confirm
        if (rotationConfirmBtn) {
            this.addEventListener(rotationConfirmBtn, 'click', () => {
            const degrees = parseInt(rotationSlider.value);
            const currentImage = this.imageManager.getCurrentImage();
            if (currentImage) {
                currentImage.initialRotation = degrees;
                this.uiController.updateStatus(`✅ Rotation sauvegardée: ${degrees}°`, 'success');
            }

            // Re-enable controls
            this.sceneManager.setControlsEnabled(true);

            // Disable cursor message
            this.disableCorrectionCursorMessage();

            // Hide panel and restore sidebar
            rotationPanel.classList.add('hidden');
            this.sandboxPanel?.restore();
            });
        }

        // Rotation cancel
        if (rotationCancelBtn) {
            this.addEventListener(rotationCancelBtn, 'click', () => {
            // Restore original rotation to camera
            const currentImage = this.imageManager.getCurrentImage();
            if (currentImage) {
                const originalRotation = currentImage.initialRotation || 0;
                const radians = (originalRotation * Math.PI) / 180;
                this.sceneManager.setCameraRotation(radians);
            }

            // Re-enable controls
            this.sceneManager.setControlsEnabled(true);

            // Disable cursor message
            this.disableCorrectionCursorMessage();

            // Hide panel and restore sidebar
            rotationPanel.classList.add('hidden');
            this.sandboxPanel?.restore();
            this.uiController.updateStatus('❌ Rotation annulée', 'error');
            });
        }

        // Correct brightness button
        if (correctBrightnessBtn) {
            this.addEventListener(correctBrightnessBtn, 'click', () => {
            const currentImage = this.imageManager.getCurrentImage();
            if (!currentImage) return;

            // Hide tools panel
            toolsPanel.classList.add('hidden');
            toolsPanelOpen = false;

            // Set slider to current brightness
            const currentBrightness = currentImage.brightness || 100;
            brightnessSlider.value = currentBrightness.toString();
            brightnessValue.textContent = `${currentBrightness}%`;

            // Show panel and reduce sidebar
            brightnessPanel.classList.remove('hidden');
            this.sandboxPanel?.minimize();
            });
        }

        // Brightness slider real-time update
        if (brightnessSlider) {
            this.addEventListener(brightnessSlider, 'input', (e) => {
                const brightness = parseInt(e.target.value);
                this.sphereManager.setBrightness(brightness);
                brightnessValue.textContent = `${brightness}%`;
            });
        }

        // Brightness confirm
        if (brightnessConfirmBtn) {
            this.addEventListener(brightnessConfirmBtn, 'click', () => {
            const brightness = parseInt(brightnessSlider.value);
            const currentImage = this.imageManager.getCurrentImage();
            if (currentImage) {
                currentImage.brightness = brightness;
                this.uiController.updateStatus(`✅ Luminosité sauvegardée: ${brightness}%`, 'success');
            }

            // Hide panel and restore sidebar
            brightnessPanel.classList.add('hidden');
            this.sandboxPanel?.restore();
            });
        }

        // Brightness cancel
        if (brightnessCancelBtn) {
            this.addEventListener(brightnessCancelBtn, 'click', () => {
            // Restore original brightness
            const currentImage = this.imageManager.getCurrentImage();
            if (currentImage) {
                const originalBrightness = currentImage.brightness || 100;
                this.sphereManager.setBrightness(originalBrightness);
            }

            // Hide panel and restore sidebar
            brightnessPanel.classList.add('hidden');
            this.sandboxPanel?.restore();
            this.uiController.updateStatus('❌ Luminosité annulée', 'error');
            });
        }

        // Apply brightness when image changes
        const originalOnImageChanged = this.imageManager.onImageChanged;
        this.imageManager.onImageChanged = (index) => {
            if (originalOnImageChanged) {
                originalOnImageChanged(index);
            }

            // Apply brightness for current image
            const currentImage = this.imageManager.images[index];
            if (currentImage) {
                const brightness = currentImage.brightness || 100;
                this.sphereManager.setBrightness(brightness);
            }
        };

        console.log('✅ Image correction tools setup');
    }

    /**
     * Convert blob URL or image element to base64 data URL
     * @param {string|HTMLImageElement} imageSource - Image source (blob URL, data URL, or HTMLImageElement)
     * @returns {Promise<string>} Base64 data URL
     */
    async imageToDataURL(imageSource) {
        // If already a data URL, return as is
        if (typeof imageSource === 'string' && imageSource.startsWith('data:')) {
            return imageSource;
        }

        // If it's an https URL (from API), return as is
        if (typeof imageSource === 'string' && (imageSource.startsWith('http://') || imageSource.startsWith('https://'))) {
            return imageSource;
        }

        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.crossOrigin = 'anonymous';

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                try {
                    const dataURL = canvas.toDataURL('image/jpeg', 0.95);
                    resolve(dataURL);
                } catch (error) {
                    console.error('Error converting image to data URL:', error);
                    reject(error);
                }
            };

            img.onerror = (error) => {
                console.error('Error loading image:', error);
                reject(error);
            };

            // Handle different input types
            if (typeof imageSource === 'string') {
                img.src = imageSource;
            } else if (imageSource instanceof HTMLImageElement) {
                img.src = imageSource.src;
            } else {
                reject(new Error('Invalid image source'));
            }
        });
    }

    /**
     * Build the complete virtual tour JSON for API export
     * @returns {Promise<Object>} Complete tour data
     */
    async buildVirtualTourJSON() {
        const data = {
            title: this.projectTitle, // Add project name/title
            pictures: [],
            graph: {
                connections: [],
                customImages: [],
                nodes: [],
                tripodMasks: {
                    enabled: this.tripodMaskManager.hasMask(),
                    imageUrl: null,
                    size: this.tripodMaskManager.maskSize || 1.0
                },
                logoOverlay: {
                    enabled: false,
                    imageUrl: null,
                    position: 'top-left'
                }
            }
        };

        // Convert tripod mask to data URL if exists
        if (this.tripodMaskManager.hasMask() && this.tripodMaskManager.maskTexture?.image?.src) {
            try {
                data.graph.tripodMasks.imageUrl = await this.imageToDataURL(this.tripodMaskManager.maskTexture.image.src);
            } catch (error) {
                console.error('Error converting tripod mask:', error);
            }
        }

        // Convert logo overlay to data URL if exists
        const logoData = this.logoOverlayManager.exportData();
        if (logoData?.enabled && logoData.imageUrl) {
            try {
                data.graph.logoOverlay = {
                    enabled: logoData.enabled,
                    imageUrl: await this.imageToDataURL(logoData.imageUrl),
                    position: logoData.position
                };
            } catch (error) {
                console.error('Error converting logo:', error);
            }
        }

        // Add pictures with rotation and brightness - convert URLs to data URLs
        for (const img of this.imageManager.images) {
            const picture = {
                id: img.id,
                filename: img.name,
                url: img.url, // Will be converted below
                initialRotation: img.initialRotation || 0,
                brightness: img.brightness || 100
            };

            // Convert blob/image to data URL
            try {
                if (img.texture?.image) {
                    picture.url = await this.imageToDataURL(img.texture.image);
                } else if (img.url) {
                    picture.url = await this.imageToDataURL(img.url);
                }
            } catch (error) {
                console.error(`Error converting picture ${img.name}:`, error);
                // Keep original URL if conversion fails
            }

            // Add alias only if set and different from filename
            if (img.alias && img.alias !== img.name) {
                picture.alias = img.alias;
            }

            // Add picture ID mapping if available (for API-loaded tours)
            if (img.pictureId) {
                picture.pictureId = img.pictureId;
            }

            data.pictures.push(picture);
        }

        // Add graph node positions from GraphManager
        if (this.graphManager) {
            const graphData = this.graphManager.exportData();
            data.graph.nodes = graphData.nodes.map(node => ({
                imageId: node.imageId,
                x: node.x,
                y: node.y,
                locked: node.locked || false,
                label: node.label
            }));
        }

        // Add connections from navigation manager
        // For each connection, save BOTH directions (bidirectional navigation)
        let connectionIdCounter = 1;
        this.navigationManager.connections.forEach(conn => {
            // Direction 1: from startImage to endImage
            data.graph.connections.push({
                id: connectionIdCounter++,
                from: conn.startImage.id,
                to: conn.endImage.id,
                startPosition: {
                    yaw: conn.startYaw,
                    pitch: conn.startPitch
                },
                endPosition: {
                    yaw: conn.endYaw || 0,
                    pitch: conn.endPitch || 0
                },
                type: 'navigation'
            });

            // Direction 2: from endImage to startImage (reverse connection)
            data.graph.connections.push({
                id: connectionIdCounter++,
                from: conn.endImage.id,
                to: conn.startImage.id,
                startPosition: {
                    yaw: conn.endYaw || 0,
                    pitch: conn.endPitch || 0
                },
                endPosition: {
                    yaw: conn.startYaw,
                    pitch: conn.startPitch
                },
                type: 'navigation'
            });
        });

        // Add custom images (hotspots) from hotspot manager - convert URLs to data URLs
        for (const hotspot of this.hotspotManager.hotspots) {
            const customImage = {
                id: hotspot.id,
                imageId: hotspot.imageId,
                url: null,
                position: {
                    yaw: hotspot.yaw,
                    pitch: hotspot.pitch
                },
                size: hotspot.size || 'medium'
            };

            // Convert blob/image to data URL
            try {
                if (hotspot.texture?.image?.src) {
                    customImage.url = await this.imageToDataURL(hotspot.texture.image.src);
                }
            } catch (error) {
                console.error(`Error converting hotspot ${hotspot.id}:`, error);
            }

            // Add name only if set
            if (hotspot.imageName) {
                customImage.name = hotspot.imageName;
            }

            data.graph.customImages.push(customImage);
        }

        return data;
    }

    /**
     * Start graph connection mode
     */
    startGraphConnectionMode() {
        if (!this.graphManager) return;

        const currentImage = this.imageManager.getCurrentImage();
        if (!currentImage) {
            this.uiController.updateStatus('❌ No image selected', 'error');
            return;
        }

        // Find the node for the current image
        const sourceNode = this.graphManager.nodes.find(n => n.imageId === currentImage.id);
        if (!sourceNode) {
            this.uiController.updateStatus('❌ Current image not found in graph', 'error');
            return;
        }

        // Show viewer overlay to block interactions
        if (this.viewerOverlay) {
            this.viewerOverlay.show(() => this.cancelConnection());
        }

        // Start connection mode in graph
        this.graphManager.startConnectionMode(sourceNode);
        this.uiController.updateStatus('🎯 Sélectionnez une image de destination dans le graph', 'success');
    }

    /**
     * Handle connection request from graph
     */
    handleGraphConnectionRequest(sourceNode, destNode) {
        console.log('[App] Connection requested:', sourceNode.label, '->', destNode.label);

        // Hide viewer overlay now that selection is done
        if (this.viewerOverlay) {
            this.viewerOverlay.hide();
        }

        // Find the image data for destination
        const destImage = this.imageManager.images.find(img => img.id === destNode.imageId);
        if (!destImage) {
            this.uiController.updateStatus('❌ Destination image not found', 'error');
            return;
        }

        // Store destination and start placement
        this.selectedConnectionEndImage = destImage;

        const currentImage = this.imageManager.getCurrentImage();
        const raycastTargets = this.sphereManager.getRaycastObjects();

        this.navigationManager.startCreation(
            currentImage,
            this.selectedConnectionEndImage,
            raycastTargets,
            (cursor) => this.sceneManager.setCursor(cursor)
        );

        // Update graph help text for placement phase
        if (this.graphManager) {
            this.graphManager.setHelpText('🎯 Placement du point de connexion en cours... (Le bouton "Annuler" reste disponible)', 'info');
        }

        // Minimize sidebar for more space
        if (this.sandboxPanel) {
            this.sandboxPanel.minimize();
        }

        this.uiController.updateStatus('🎯 Placez le point de départ de la connexion (Échap pour annuler)', 'success');
    }

    /**
     * Check if current mode is readonly
     */
    isReadOnly() {
        return this.mode === 'view';
    }

    /**
     * Check if current mode is sandbox
     */
    isSandbox() {
        return this.mode === 'edit';
    }

    /**
     * Show loading overlay with custom message
     * @param {string} message - Loading message to display
     */
    showLoader(message = 'Chargement...') {
        const overlay = document.getElementById('loading-overlay');
        const text = document.getElementById('loading-text');

        if (overlay) {
            if (text) text.textContent = message;
            overlay.classList.remove('hidden');
        }
    }

    /**
     * Hide loading overlay
     */
    hideLoader() {
        const overlay = document.getElementById('loading-overlay');
        if (overlay) {
            overlay.classList.add('hidden');
        }
    }

    /**
     * Enable correction cursor message (shows message when user tries to interact)
     */
    enableCorrectionCursorMessage() {
        const message = document.getElementById('correction-cursor-message');
        const canvas = document.querySelector('canvas');

        if (!message || !canvas) return;

        // Track mouse movement over canvas
        this._cursorMessageHandler = (e) => {
            message.style.left = `${e.clientX + 15}px`;
            message.style.top = `${e.clientY + 15}px`;
        };

        // Show message when mouse enters canvas
        this._cursorEnterHandler = () => {
            message.classList.remove('hidden');
        };

        // Hide message when mouse leaves canvas
        this._cursorLeaveHandler = () => {
            message.classList.add('hidden');
        };

        this.addEventListener(canvas, 'mousemove', this._cursorMessageHandler);
        this.addEventListener(canvas, 'mouseenter', this._cursorEnterHandler);
        this.addEventListener(canvas, 'mouseleave', this._cursorLeaveHandler);
    }

    /**
     * Disable correction cursor message
     */
    disableCorrectionCursorMessage() {
        const message = document.getElementById('correction-cursor-message');
        const canvas = document.querySelector('canvas');

        if (!message || !canvas) return;

        // Remove event listeners
        if (this._cursorMessageHandler) {
            canvas.removeEventListener('mousemove', this._cursorMessageHandler);
        }
        if (this._cursorEnterHandler) {
            canvas.removeEventListener('mouseenter', this._cursorEnterHandler);
        }
        if (this._cursorLeaveHandler) {
            canvas.removeEventListener('mouseleave', this._cursorLeaveHandler);
        }

        // Hide message
        message.classList.add('hidden');
    }

    /**
     * Show initial upload modal for creation mode (sandbox without experienceId)
     * This is called by Viewer360 when initializing in edit mode without an experienceId
     */
    showInitialUploadModal() {
        const modal = document.getElementById('initial-upload-modal');
        const uploadBtn = document.getElementById('initial-upload-btn');
        const fileInput = document.getElementById('initial-file-input');

        if (!modal || !uploadBtn || !fileInput) {
            console.error('[App] Initial upload modal elements not found');
            return;
        }

        // Show modal
        modal.classList.remove('hidden');

        // Handle file selection (remove old listeners first)
        const newFileInput = fileInput.cloneNode(true);
        fileInput.parentNode.replaceChild(newFileInput, fileInput);

        // Setup upload button (remove old listeners first)
        const newUploadBtn = uploadBtn.cloneNode(true);
        uploadBtn.parentNode.replaceChild(newUploadBtn, uploadBtn);

        newUploadBtn.addEventListener('click', () => {
            newFileInput.click(); // Click on the NEW input, not the old one
        });

        newFileInput.addEventListener('change', async (event) => {
            const file = event.target.files[0];
            if (!file) return;

            console.log(`[App] Initial image selected: ${file.name}`);

            // Hide modal
            modal.classList.add('hidden');

            // Load the initial image
            if (this.handleImageSelection) {
                const syntheticEvent = { target: { files: [file] } };
                await this.handleImageSelection(syntheticEvent);
            }
        });
    }

    /**
     * Public method to save the virtual tour
     * Called by Viewer360.save()
     * @returns {Promise<Object>} API response
     */
    async saveVirtualTour() {
        if (this.mode !== 'edit') {
            throw new Error('Save is only available in sandbox mode');
        }

        console.log('[App] 💾 Save requested from Viewer360...');

        try {
            // STEP 1: Show preparing step
            this.saveProgressOverlay.showPreparingStep();

            // Build JSON data
            const data = await this.buildVirtualTourJSON();
            console.log('[App] Generated JSON:', data);

            // Count images for progress tracking
            const imageCount = data.pictures?.length || 0;

            // STEP 2: Show optimizing step (initial state)
            this.saveProgressOverlay.showOptimizingStep(0, imageCount);

            // Simulate progress updates (1.5-2s per image)
            let imagesProcessed = 0;
            const progressInterval = setInterval(() => {
                if (imagesProcessed < imageCount) {
                    imagesProcessed++;
                    this.saveProgressOverlay.showOptimizingStep(imagesProcessed, imageCount);
                }
            }, 1750); // ~1.75s per image average

            // Call API to save
            // Use galleryId if we already have one (update), otherwise create new (no galleryId param)
            const response = await this.apiService.saveVirtualTour(data, this.galleryId);
            console.log('[App] API Response:', response);

            // Store galleryId and sceneId from the response for future updates
            if (response.galleryId) {
                this.galleryId = response.galleryId;
                console.log(`[App] 📌 Gallery ID stored: ${this.galleryId}`);
            }
            if (response.sceneId) {
                this.sceneId = response.sceneId;
                console.log(`[App] 📌 Scene ID stored: ${this.sceneId} (CDN path: gallery_360/${this.sceneId})`);
            }

            // Clear the progress interval
            clearInterval(progressInterval);

            // STEP 3: Show completion
            this.saveProgressOverlay.showCompleteStep();

            // Show success status
            if (this.uiController) {
                const saveType = this.galleryId ? 'mise à jour' : 'sauvegarde';
                this.uiController.updateStatus(`✅ Visite virtuelle ${saveType} avec succès`, 'success');
            }

            // Mark as saved (resets hasUnsavedChanges and notifies parent)
            this.markAsSaved();

            // Hide overlay after 1.5 seconds
            setTimeout(() => {
                this.saveProgressOverlay.hide();
            }, 1500);

            return response;

        } catch (error) {
            console.error('[App] Save error:', error);

            // Show error in overlay
            const errorMessage = error.message || 'Erreur inconnue';
            this.saveProgressOverlay.showErrorStep(`Erreur lors de la sauvegarde: ${errorMessage}`);

            // Show error status
            if (this.uiController) {
                this.uiController.updateStatus(`❌ Erreur lors de la sauvegarde: ${errorMessage}`, 'error');
            }

            // Hide overlay after 3 seconds
            setTimeout(() => {
                this.saveProgressOverlay.hide();
            }, 3000);

            throw error;
        }
    }

    /**
     * Cleanup and dispose resources
     */
    dispose() {
        console.log('[App] Disposing application...');

        // Remove all tracked event listeners
        this.removeAllEventListeners();

        // Cleanup cursor message if active
        this.disableCorrectionCursorMessage();

        // Dispose managers
        if (this.imageManager) {
            this.imageManager.dispose();
        }

        if (this.hotspotManager) {
            this.hotspotManager.dispose();
        }

        if (this.navigationManager) {
            this.navigationManager.dispose();
        }

        if (this.tripodMaskManager) {
            this.tripodMaskManager.dispose();
        }

        if (this.graphManager && typeof this.graphManager.dispose === 'function') {
            this.graphManager.dispose();
        }

        if (this.sandboxPanel && typeof this.sandboxPanel.dispose === 'function') {
            this.sandboxPanel.dispose();
        }

        if (this.logoOverlayManager) {
            this.logoOverlayManager.dispose();
        }

        if (this.galleryManager && typeof this.galleryManager.dispose === 'function') {
            this.galleryManager.dispose();
        }

        // Dispose UI components
        if (this.crosshairOverlay) {
            this.crosshairOverlay.dispose();
        }

        if (this.placementControls) {
            this.placementControls.dispose();
        }

        if (this.viewerOverlay) {
            this.viewerOverlay.dispose();
        }

        if (this.saveProgressOverlay) {
            this.saveProgressOverlay.dispose();
        }

        if (this.toast) {
            this.toast.dispose();
        }

        // Dispose core components (should be last)
        if (this.sceneManager) {
            this.sceneManager.dispose();
        }

        // Clean up DOM elements we created
        if (this.customImageInput && this.customImageInput.parentNode) {
            this.customImageInput.parentNode.removeChild(this.customImageInput);
        }

        console.log('[App] Application disposed successfully');
    }
}

